import { ManquesFiche } from '../ManquesFiche.js';
import { InventoryNumber, Price } from '@artosera/ui';
import type { ActionLotOeuvres, LigneLotOeuvres } from '@artosera/shared';
import { ArrowRight } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LotOeuvresDialog.module.css';

type Langue = 'fr' | 'en';

function texte(valeur: unknown): string | null {
  return typeof valeur === 'string' ? valeur : null;
}

function prixDe(valeur: LigneLotOeuvres['avant']): { cents: number; devise: string } | null {
  const cents = valeur?.['prixPublicCentimes'];
  const devise = valeur?.['devise'];
  return typeof cents === 'number' && typeof devise === 'string' ? { cents, devise } : null;
}

/** Ce qui change sur une ligne : « avant → après », ou le motif du refus. */
function Changement({
  ligne,
  type,
  langue,
  nomLieu,
}: {
  ligne: LigneLotOeuvres;
  type: ActionLotOeuvres;
  langue: Langue;
  nomLieu: (id: unknown) => string;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres']);
  if (ligne.resultat === 'refusee') {
    return (
      <span className={styles.motif}>{ligne.motif ?? t('regie:oeuvres.lot.ligne.refusee')}</span>
    );
  }
  if (ligne.resultat === 'inchangee') {
    return <span className={styles.discret}>{t('regie:oeuvres.lot.ligne.inchangee')}</span>;
  }
  let avant: JSX.Element | string | null = null;
  let apres: JSX.Element | string | null = null;
  switch (type) {
    case 'prix': {
      const a = prixDe(ligne.avant);
      const b = prixDe(ligne.apres);
      avant = a ? <Price cents={a.cents} lang={langue} currency={a.devise} /> : null;
      apres = b ? <Price cents={b.cents} lang={langue} currency={b.devise} /> : null;
      break;
    }
    case 'statut':
    case 'archivage': {
      const a = texte(ligne.avant?.['statut']);
      const b = texte(ligne.apres?.['statut']);
      avant = a ? t(`oeuvres:statuts.${a}`) : null;
      apres = b ? t(`oeuvres:statuts.${b}`) : null;
      break;
    }
    case 'publication': {
      const a = ligne.avant?.['visibleSurLeSite'];
      const b = ligne.apres?.['visibleSurLeSite'];
      avant = typeof a === 'boolean' ? t(`regie:oeuvres.lot.ligne.visible.${String(a)}`) : null;
      apres = typeof b === 'boolean' ? t(`regie:oeuvres.lot.ligne.visible.${String(b)}`) : null;
      break;
    }
    case 'localisation':
      avant = nomLieu(ligne.avant?.['localisationId']);
      apres = nomLieu(ligne.apres?.['localisationId']);
      break;
    case 'exposition':
    case 'selection':
      return <span>{t('regie:oeuvres.lot.ligne.ajoutee')}</span>;
  }
  return (
    <span className={styles.avantApres}>
      <span className={styles.avant}>{avant ?? '—'}</span>
      <ArrowRight size={14} strokeWidth={1.5} aria-label={t('regie:oeuvres.lot.ligne.devient')} />
      <span className={styles.apres}>{apres ?? '—'}</span>
    </span>
  );
}

/** Le tableau de l'aperçu, ligne à ligne, dans l'ordre des œuvres envoyées. */
export function TableauApercuLot({
  lignes,
  type,
  langue,
  lieuParId,
}: {
  lignes: readonly LigneLotOeuvres[];
  type: ActionLotOeuvres;
  langue: Langue;
  lieuParId: ReadonlyMap<string, string>;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const nomLieu = (id: unknown): string =>
    typeof id === 'string' ? (lieuParId.get(id) ?? '—') : t('oeuvres.lot.champs.sansLieu');
  return (
    <div className={styles.defilement} tabIndex={0} aria-label={t('oeuvres.lot.apercu.tableau')}>
      <table className={styles.tableau}>
        <thead>
          <tr>
            <th scope="col">{t('oeuvres.lot.apercu.colonneOeuvre')}</th>
            <th scope="col">{t('oeuvres.lot.apercu.colonneChangement')}</th>
            <th scope="col">{t('oeuvres.lot.apercu.colonneResultat')}</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((ligne) => (
            <tr key={ligne.oeuvreId} data-resultat={ligne.resultat}>
              <td className={styles.celluleOeuvre}>
                <span className={styles.titreLigne}>
                  {ligne.titre ?? t('oeuvres.lot.ligne.introuvable')}
                </span>
                {ligne.numeroInventaire ? <InventoryNumber value={ligne.numeroInventaire} /> : null}
              </td>
              <td data-label={t('oeuvres.lot.apercu.colonneChangement')}>
                <Changement ligne={ligne} type={type} langue={langue} nomLieu={nomLieu} />
                <ManquesFiche manques={ligne.manques ?? []} />
              </td>
              <td className={styles.celluleResultat}>
                <span className={styles.resultat} data-resultat={ligne.resultat}>
                  {t(`oeuvres.lot.resultat.${ligne.resultat}`)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
